import { ExpandedSurvey, NotificationGroup, Root, TargetFieldRef } from '../survey';
import { DependencyMap, DependencyNode, TRANSLATORS } from './questions';

/**
 * Build a dependency map from a survey by iterating through all components
 * and calling their calculateDependencies translators.
 */
export function getDependencyMap(
  survey: (Root & { __templates_expanded: true }) | ExpandedSurvey
): DependencyMap {
  const map = new Map<TargetFieldRef, DependencyNode>();
  // Extract enableLastModifiedInJS from config, mirroring the pattern in v0ToLegacy
  // If survey is ExpandedSurvey (not an array), it may have config
  const enableLastModifiedInJS = !Array.isArray(survey) && survey?.config?.experimental?.enableLastModifiedInJS;
  const context = { enableLastModifiedInJS };

  const surveyComponents = (survey && Array.isArray(survey) ? survey : survey?.survey) ?? [];
  for (const component of surveyComponents) {
    const translator = TRANSLATORS[component.kind as keyof typeof TRANSLATORS];
    if (translator && 'calculateDependencies' in translator) {
      (translator as any).calculateDependencies(component, map, context);
    }
  }
  return map;
}

/**
 * Collects and returns any NotificationGroup notifs that depend (including transitively) on the provided targetField.
 * Only considers "formula" type dependencies, not content or conditionals.
 */
export function findAnyDownstreamNotifications(
  map: DependencyMap,
  startId: TargetFieldRef
): NotificationGroup[] {
  // Nodes/fields we've seen already
  const visited = new Set<TargetFieldRef>();
  const downstreamNotifications: NotificationGroup[] = [];
  // Stack for depth-first-search
  const stack = [startId];
  while (stack.length > 0) {
    const currentId = stack.pop()!;
    // Skip if we've already visited
    if (!currentId || visited.has(currentId)) {
      continue;
    }
    visited.add(currentId);
    const currentNode = map.get(currentId);
    if (!currentNode) {
      continue;
    }
    if (
      currentNode.component &&
      currentNode.component.kind === "Notification"
    ) {
      downstreamNotifications.push(currentNode.component);
    }
    for (const child of currentNode.children) {
      if (child.type !== "formula") continue;
      // This likely indicates a misconfiguration in Distro
      if (!child.field) {
        const errorMsg = `Empty reference of type "${child.type}" found for ${currentId}${currentNode.component ? ' kind: "' + currentNode.component.kind + '"' : ""}`;
        console.warn(errorMsg);
        continue;
      }
      // Continue traversal if we haven't seen the relation yet
      if (!visited.has(child.field)) {
        stack.push(child.field);
      }
    }
  }
  return downstreamNotifications;
}
